import { ERROR_MESSAGES } from '../../../reducers/helpers';
import { storageAvailable } from '../../../helpers';
import { trackRegistrationError } from '../../utils';

const reducers = {
  appAccessRequestSuccess: (app, payload: { accessToken: string }) => ({
    ...app,
    regAccessToken: payload.accessToken
  }),

  appCheckPhoneSuccess: (app, payload: { regAccessToken: string }) => ({
    ...app,
    shouldShowLoader: false,
    regAccessToken: payload.regAccessToken
  }),

  appConfirmCodeSuccess: (app, payload: { accessToken: string }) => ({
    ...app,
    regAccessToken: payload.accessToken
  }),

  appForgotPasswordSuccess: (app, payload: { message: string }) => ({
    ...app,
    message: payload.message
  }),

  appLoginFailure: (app, payload: { response?: any }) => {
    let loginErrorMessage = '';
    if (payload.response) {
      loginErrorMessage =
        payload.response.data.code === 'ERR_DEACTIVATED'
          ? ERROR_MESSAGES.loginFailureErrDeactivated
          : payload.response.data.message;
      trackRegistrationError({
        eventProps: {
          registration_step: 'login',
          error_message: loginErrorMessage
        }
      });
    }

    return {
      ...app,
      message: loginErrorMessage
    };
  },

  appLoginSuccess: (app, payload: { user: any; token: string }) => {
    const { user } = payload;
    const nextRegStep = user.nextRegisterStep;
    let completeOnInit = false;

    if (
      user.role === 'parent' &&
      !user.children.some(
        child => child.nextRegisterStep === 'child_pending_parent_completion'
      )
    ) {
      // User is returning parent not answering child invite.
      return {
        ...app,
        shouldRedirectToLta: true,
        ltaRedirectParams: {
          role: user.role,
          phoneNumber: user.phoneNumber,
        }
      };
    }

    if (nextRegStep === 'deactivated') {
      trackRegistrationError({
        eventProps: {
          registration_step: 'login',
          error_message: ERROR_MESSAGES.loginFailureErrDeactivated
        }
      });

      return {
        ...app,
        error: true,
        message: ERROR_MESSAGES.loginFailureErrDeactivated
      };
    }

    if (payload.user.nextRegisterStep === 'complete') {
      completeOnInit = true;
      if (storageAvailable('sessionStorage')) {
        sessionStorage.setItem('userCompleted', JSON.stringify(true));
      }
    }

    return {
      ...app,
      completeOnInit,
      togglesDirty: true,
      shouldRefetchServersideToggles: true,
      stargatewayKey: payload.token ? payload.token : app.stargatewayKey,
      isFirstScreenForReturningUser:
        nextRegStep !== 'complete' && nextRegStep !== 'deactivated'
    };
  },

  appResetPasswordSuccess: (
    app,
    payload: {
      message: string;
      accessToken: string;
    }
  ) => ({
    ...app,
    message: payload.message,
    regAccessToken: payload.accessToken
  }),

  appValidatePlaceholderTokenFailure: app => ({
    ...app,
    shouldBounceInvitedUsersWhoDidntUseInviteLink: true
  }),

  appValidatePlaceholderTokenSuccess: (app, payload: { user: any }) => ({
    ...app,
    shouldBounceInvitedUsersWhoDidntUseInviteLink: payload.user
      ? payload.user.id !== app.preregisteredUser.id
      : true
  }),

  appVoicePinSuccess: app => ({
    ...app,
    message: 'Calling! Please enter confirmation code below'
  }),

  appVoicePinFailure: (app, payload: { error: any }) => {
    const voicePinErrorMessage = payload.error.response
      ? payload.error.response.data.message
      : '';
    return {
      ...app,
      message: voicePinErrorMessage
    };
  },

  // async logic passthrough reducers
  checkPhone: (
    app,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    payload: { phoneNumber: string; deviceToken: string }
  ) => app,
  accessRequest: (
    app,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    payload: { phoneNumber: string; deviceUuid: string; pinLength: number }
  ) => app,
  confirmCode: (
    app,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    payload: { phoneNumber: string; deviceUuid: string; code: string }
  ) => app,
  forgotPassword: (
    app,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    payload: { email: string; accessToken: string }
  ) => app,
  login: (
    app,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    payload: { phoneNumber: string; password: string; accessToken: string }
  ) => app,
  resetPassword: (
    app,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    payload: {
      userId: string;
      token: string;
      deviceUuid: string;
      newPassword: string;
      verifyPassword: string;
    }
  ) => app,
  voicePin: (
    app,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    payload: {
      phoneNumber: string;
      deviceUuid: string;
    }
  ) => app,
  validateResetPasswordToken: (app, payload: { url: string }) => app,
  appValidateResetPasswordTokenSuccess: (app, payload: {}) => {
    return { ...app, validateResetPasswordTokenComplete: true };
  },
  appValidateResetPasswordTokenFailure: (app, payload: {}) => ({
    ...app,
    error: true,
    validateResetPasswordTokenComplete: true,
    redirectOnError: true
  }),
  ltaResetPassword: (
    app,
    payload: { url: string; device_id: string; password: string }
  ) => app,
  appLtaResetPasswordSuccess: (
    app,
    payload: {
      message: string;
    }
  ) => ({
    ...app,
    message: payload.message,
    error: false
  }),
  appLtaResetPasswordFailure: (app, payload: { response?: any }) => {
    return {
      ...app,
      error: true,
      redirectOnError: payload.response.status !== 422
    };
  }
};

export default { ...reducers };
